import React, { useMemo, useRef, useState } from 'react';
import { ICampaign, ICampaignListResult } from 'core';
import {
    CheckCircle2, CircleDot, Megaphone, PauseCircle, XCircle,
    ChevronLeft, ChevronRight, Calendar, Tag, Mail, CreditCard, Clock,
} from 'lucide-react';

export interface TimelineEvent {
    id: string;
    type: string;
    timestamp: Date;
    title: string;
    description: string;
    icon: string;
    color: string;
    metadata: Record<string, any>;
    relatedEvents?: string[];
}

export interface TimelineFilters {
    dateRange?: { start: Date; end: Date };
    eventTypes?: string[];
    searchText?: string;
}

export interface TimelineProps {
    campaignLoading?: boolean;
    campaignData?: ICampaignListResult;
    events: TimelineEvent[];
    context: 'ticket' | 'billing' | 'template' | 'campaign' | 'global';
    filters: TimelineFilters;
    onEventClick: (event: TimelineEvent) => void;
    onFilterChange: (filters: TimelineFilters) => void;
    customRenderers?: Record<string, (event: TimelineEvent) => React.ReactNode>;
    showAnalytics?: boolean;
    height?: number;
    className?: string;
    itemSize?: number;
}

const STATUS: Record<string, {
    hex: string; bg: string; text: string;
    darkBg: string; darkText: string;
    icon: React.FC<{ size?: number; strokeWidth?: number }>; label: string;
    gradientFrom: string; gradientTo: string;
}> = {
    active:     {
        hex: '#16a34a', bg: 'bg-emerald-50 dark:bg-emerald-900/30', text: 'text-emerald-700 dark:text-emerald-300',
        darkBg: 'dark:bg-emerald-900/25', darkText: 'dark:text-emerald-300',
        icon: ({ size = 12, strokeWidth = 2.5 }) => <Megaphone    size={size} strokeWidth={strokeWidth} />,
        label: 'Active',
        gradientFrom: '#16a34a', gradientTo: '#0d9488',
    },
    draft:      {
        hex: '#6b7280', bg: 'bg-surface-sunken', text: 'text-content-secondary',
        darkBg: 'dark:bg-white/[0.07]', darkText: 'dark:text-gray-300',
        icon: ({ size = 12, strokeWidth = 2.5 }) => <CircleDot    size={size} strokeWidth={strokeWidth} />,
        label: 'Draft',
        gradientFrom: '#6b7280', gradientTo: '#4b5563',
    },
    paused:     {
        hex: '#d97706', bg: 'bg-amber-50 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-300',
        darkBg: 'dark:bg-amber-900/25', darkText: 'dark:text-amber-300',
        icon: ({ size = 12, strokeWidth = 2.5 }) => <PauseCircle  size={size} strokeWidth={strokeWidth} />,
        label: 'Paused',
        gradientFrom: '#d97706', gradientTo: '#f59e0b',
    },
    completed:  {
        hex: '#2563eb', bg: 'bg-blue-50 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-300',
        darkBg: 'dark:bg-blue-900/25', darkText: 'dark:text-blue-300',
        icon: ({ size = 12, strokeWidth = 2.5 }) => <CheckCircle2 size={size} strokeWidth={strokeWidth} />,
        label: 'Completed',
        gradientFrom: '#2563eb', gradientTo: '#6366f1',
    },
    terminated: {
        hex: '#dc2626', bg: 'bg-rose-50 dark:bg-rose-900/30', text: 'text-rose-700 dark:text-rose-300',
        darkBg: 'dark:bg-rose-900/25', darkText: 'dark:text-rose-300',
        icon: ({ size = 12, strokeWidth = 2.5 }) => <XCircle      size={size} strokeWidth={strokeWidth} />,
        label: 'Terminated',
        gradientFrom: '#dc2626', gradientTo: '#be185d',
    },
};

/* ── Inline meta chip ── */
const MetaChip: React.FC<{ icon: React.ReactNode; label: string; value: string; title?: string }> = ({
    icon, label, value, title,
}) => (
    <div className="flex items-center gap-2 min-w-0">
        <div className="flex items-center gap-1 text-content-muted dark:text-gray-300 flex-shrink-0">
            {icon}
            <span className="text-[10px] font-bold uppercase tracking-[0.08em] hidden sm:inline">{label}</span>
        </div>
        <span
            className="text-[12.5px] font-semibold text-gray-700 dark:text-gray-200 truncate"
            title={title || value}
        >
            {value}
        </span>
    </div>
);

/* ─── Campaign Card ─── */
const NewTimeLineItem: React.FC<{ data: ICampaign; index: number }> = ({ data, index }) => {
    const key = String(data.c_status || '').toLowerCase();
    const s   = STATUS[key] ?? STATUS['draft'];

    const start   = data.s_date ? new Date(data.s_date) : new Date();
    const title   = data.name || 'Untitled campaign';
    const email   = data.email_used || (data.c_grp ? `Group ${data.c_grp}` : '—');
    const credits = typeof data.credits_used === 'number' ? data.credits_used.toLocaleString() : '—';
    const template = data.temp_name || '—';

    const dateStr = start.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
    const timeStr = start.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });

    return (
        <div
            className="group relative mb-3 rounded-2xl overflow-hidden
                       bg-surface dark:bg-dark-800
                       border border-line dark:border-white/[0.10]
                       shadow-sm hover:shadow-lg dark:hover:shadow-black/40
                       hover:-translate-y-0.5 transition-all duration-200 cursor-pointer"
            tabIndex={0}
            role="button"
            aria-label={`Campaign: ${title}`}
        >
            {/* ── Top gradient accent bar ── */}
            <div
                className="h-[3px] w-full"
                style={{ background: `linear-gradient(90deg, ${s.gradientFrom}, ${s.gradientTo})` }}
            />

            {/* ── Main content row ── */}
            <div className="flex items-center gap-4 px-5 py-3.5">

                {/* Left: index number + icon block */}
                <div className="flex-shrink-0 flex flex-col items-center gap-1">
                    <div
                        className="w-10 h-10 rounded-xl flex items-center justify-center shadow-sm"
                        style={{
                            background: `linear-gradient(135deg, ${s.gradientFrom}22, ${s.gradientTo}18)`,
                            color: s.hex,
                            border: `1.5px solid ${s.hex}30`,
                        }}
                    >
                        {s.icon && <s.icon size={16} strokeWidth={2} />}
                    </div>
                    <span className="text-[10px] font-bold text-content-muted dark:text-gray-300 tabular-nums">
                        #{String(index + 1).padStart(2, '0')}
                    </span>
                </div>

                {/* Center: title + meta chips */}
                <div className="flex-1 min-w-0">
                    {/* Title row */}
                    <div className="flex items-center gap-2.5 mb-2">
                        <h3 className="text-[13.5px] font-bold text-content dark:text-white leading-tight truncate">
                            {title}
                        </h3>
                        {/* Status badge pill */}
                        <span
                            className={`inline-flex items-center gap-1.5 flex-shrink-0
                                        px-2 py-0.5 rounded-full text-[11px] font-bold
                                        border
                                        ${s.bg} ${s.text} ${s.darkBg} ${s.darkText}`}
                            style={{ borderColor: `${s.hex}35` }}
                        >
                            {key === 'active' && (
                                <span className="relative flex h-1.5 w-1.5">
                                    <span
                                        className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75"
                                        style={{ backgroundColor: s.hex }}
                                    />
                                    <span
                                        className="relative inline-flex rounded-full h-1.5 w-1.5"
                                        style={{ backgroundColor: s.hex }}
                                    />
                                </span>
                            )}
                            {s.label}
                        </span>
                    </div>

                    {/* Meta chips row */}
                    <div className="flex items-center gap-4 flex-wrap">
                        <MetaChip
                            icon={<Tag size={10} strokeWidth={2.5} />}
                            label="Template"
                            value={template}
                        />
                        <span className="w-px h-3 bg-gray-200 dark:bg-white/[0.10] flex-shrink-0" />
                        <MetaChip
                            icon={<Mail size={10} strokeWidth={2.5} />}
                            label="Email"
                            value={email}
                            title={email}
                        />
                        <span className="w-px h-3 bg-gray-200 dark:bg-white/[0.10] flex-shrink-0" />
                        <MetaChip
                            icon={<CreditCard size={10} strokeWidth={2.5} />}
                            label="Credits"
                            value={credits}
                        />
                    </div>
                </div>

                {/* Right: date + time */}
                <div className="flex-shrink-0 flex flex-col items-end gap-1 ml-2">
                    <div className="flex items-center gap-1.5 text-[11.5px] font-semibold text-content-muted dark:text-gray-400">
                        <Calendar size={11} strokeWidth={2} className="opacity-60" />
                        <time dateTime={start.toISOString()}>{dateStr}</time>
                    </div>
                    <div className="flex items-center gap-1.5 text-[10.5px] font-medium text-content-muted dark:text-gray-400">
                        <Clock size={10} strokeWidth={2} className="opacity-60" />
                        <span>{timeStr}</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export const Timeline: React.FC<TimelineProps> = ({
    events,
    context,
    filters,
    onEventClick,
    customRenderers,
    className = '',
    campaignData
}) => {
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    // Filter events
    const filteredEvents = useMemo(() => {
        let filtered = [...events];
        if (filters.dateRange) {
            filtered = filtered.filter(event =>
                event.timestamp >= filters.dateRange!.start &&
                event.timestamp <= filters.dateRange!.end
            );
        }
        if (filters.eventTypes && filters.eventTypes.length > 0) {
            filtered = filtered.filter(event => filters.eventTypes!.includes(event.type));
        }
        if (filters.searchText) {
            const s = filters.searchText.toLowerCase();
            filtered = filtered.filter(event =>
                event.title.toLowerCase().includes(s) ||
                event.description.toLowerCase().includes(s)
            );
        }
        return filtered.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
    }, [events, filters]);

    // Normalize items
    const rawItems = campaignData?.items;
    const itemsArray: ICampaign[] = Array.isArray(rawItems)
        ? rawItems
        : rawItems && typeof rawItems === 'object'
            ? (Object.values(rawItems) as ICampaign[])
            : [];

    const sortedItems = [...itemsArray].sort((a: any, b: any) =>
        new Date(b.s_date || 0).getTime() - new Date(a.s_date || 0).getTime()
    );
    const totalItems     = sortedItems.length;
    const totalPages     = Math.ceil(totalItems / itemsPerPage);
    const startIndex     = (currentPage - 1) * itemsPerPage;
    const paginatedItems = sortedItems.slice(startIndex, startIndex + itemsPerPage);

    const handlePreviousPage = () => setCurrentPage(p => Math.max(1, p - 1));
    const handleNextPage     = () => setCurrentPage(p => Math.min(totalPages, p + 1));

    // Stable callbacks
    const onEventClickRef    = useRef(onEventClick);
    const customRenderersRef = useRef(customRenderers);
    onEventClickRef.current    = onEventClick;
    customRenderersRef.current = customRenderers;
    const hasNoCampaigns = totalItems === 0;
    const hasNoEvents    = filteredEvents.length === 0;

    /* ── Empty state ── */
    if (hasNoEvents && hasNoCampaigns) {
        return (
            <div className={`rounded-xl border border-dashed border-line dark:border-white/[0.12]
                             bg-surface dark:bg-dark-800 ${className}`}>
                <div className="flex flex-col items-center justify-center py-16 px-8 text-center">
                    <div className="relative mb-5">
                        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-100
                                        dark:from-blue-900/30 dark:to-indigo-900/20 flex items-center justify-center shadow-inner
                                        border border-blue-100 dark:border-blue-800/30">
                            <svg className="w-7 h-7 text-blue-400 dark:text-blue-400" fill="none" viewBox="0 0 24 24"
                                stroke="currentColor" strokeWidth={1.5}>
                                <path strokeLinecap="round" strokeLinejoin="round"
                                    d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5
                                    7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5
                                    2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125
                                    1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
                            </svg>
                        </div>
                        <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-blue-200 dark:bg-blue-600/60" />
                        <span className="absolute -bottom-1 -left-1 w-2 h-2 rounded-full bg-indigo-200 dark:bg-indigo-600/40" />
                    </div>
                    <h3 className="text-[14px] font-bold text-content dark:text-white mb-1.5">No campaign history yet</h3>
                    <p className="text-[12.5px] text-content-muted dark:text-gray-400 max-w-xs leading-relaxed">
                        Once you create and run your first campaign, all activity — launches, A/B&nbsp;tests,
                        and performance updates — will appear here.
                    </p>
                    <div className="mt-5 flex items-center">
                        <a href="/campaigns"
                            className="inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700
                                       text-white text-[12.5px] font-semibold rounded-lg transition-colors shadow-sm">
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                            </svg>
                            Create a Campaign
                        </a>
                    </div>
                </div>
            </div>
        );
    }

    /* ── Main return ── */
    return (
        <div className={`space-y-2 ${className}`}>
            {/* Cards */}
            <div role="log" aria-label={`${context} history timeline`}>
                {paginatedItems.length > 0
                    ? paginatedItems.map((item: any, index: number) => (
                        <NewTimeLineItem key={index} data={item} index={index} />
                    ))
                    : null
                }

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="flex items-center justify-end gap-2 mt-5 pt-3.5
                                    border-t border-line-subtle dark:border-white/[0.06]">
                        {/* Prev */}
                        <button
                            onClick={handlePreviousPage}
                            disabled={currentPage === 1}
                            aria-label="Previous page"
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-medium
                                       bg-surface dark:bg-white/[0.06] border border-line dark:border-white/[0.08]
                                       text-content-secondary dark:text-gray-300
                                       hover:bg-gray-50 dark:hover:bg-white/10
                                       disabled:opacity-30 disabled:cursor-not-allowed
                                       transition-all duration-150 shadow-sm"
                        >
                            <ChevronLeft size={13} /> Previous
                        </button>

                        {/* Page numbers */}
                        <div className="flex items-center gap-1">
                            {[...Array(totalPages)].map((_, idx) => {
                                const pageNum = idx + 1;
                                const show =
                                    pageNum <= 3 ||
                                    pageNum === totalPages ||
                                    (pageNum >= currentPage - 1 && pageNum <= currentPage + 1);
                                const ellBefore = pageNum === currentPage - 2 && currentPage > 5;
                                const ellAfter  = pageNum === currentPage + 2 && currentPage < totalPages - 3;

                                if (ellBefore || ellAfter) {
                                    return <span key={pageNum} className="px-1 text-[12px] text-content-subtle dark:text-gray-600">…</span>;
                                }
                                if (show) {
                                    return (
                                        <button
                                            key={pageNum}
                                            onClick={() => setCurrentPage(pageNum)}
                                            aria-label={`Page ${pageNum}`}
                                            aria-current={currentPage === pageNum ? 'page' : undefined}
                                            className={`min-w-[30px] h-7 px-2 rounded-lg text-[12px] font-semibold
                                                        transition-all duration-150
                                                        ${currentPage === pageNum
                                                            ? 'bg-blue-600 text-white shadow-sm'
                                                            : 'bg-surface dark:bg-white/[0.06] border border-line dark:border-white/[0.08] text-content-secondary dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/10'
                                                        }`}
                                        >
                                            {pageNum}
                                        </button>
                                    );
                                }
                                return null;
                            })}
                        </div>

                        {/* Next */}
                        <button
                            onClick={handleNextPage}
                            disabled={currentPage === totalPages}
                            aria-label="Next page"
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-medium
                                       bg-surface dark:bg-white/[0.06] border border-line dark:border-white/[0.08]
                                       text-content-secondary dark:text-gray-300
                                       hover:bg-gray-50 dark:hover:bg-white/10
                                       disabled:opacity-30 disabled:cursor-not-allowed
                                       transition-all duration-150 shadow-sm"
                        >
                            Next <ChevronRight size={13} />
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Timeline;
